// SPDX-License-Identifier: AGPL-3.0-or-later
import Accessibility from '@app/features/accessibility/state/Accessibility';
import UserSettings from '@app/features/user/state/UserSettings';
import Window from '@app/features/window/state/Window';
import {StickerAnimationOptions} from '@app/vendor/constants/UserConstants';
import {useEffect, useState} from 'react';
export type ShouldAnimateKind =
	| 'avatar'
	| 'emoji'
	| 'sticker'
	| 'gif'
	| 'guild_icon'
	| 'banner'
	| 'custom_status_emoji';
export interface UseShouldAnimateOptions {
	kind: ShouldAnimateKind;
	isHovering?: boolean;
	isFocused?: boolean;
	entitlementOk?: boolean;
}
type ConnectionLike = {
	saveData?: boolean;
	addEventListener?: (event: string, cb: () => void) => void;
};
const getConnection = (): ConnectionLike | undefined => {
	const nav =
		typeof navigator === 'undefined'
			? undefined
			: (navigator as Navigator & {
					connection?: ConnectionLike;
				});
	return nav?.connection;
};
let cachedSaveData: boolean = getConnection()?.saveData === true;
const saveDataListeners = new Set<(value: boolean) => void>();
let saveDataListenerInstalled = false;
function installSaveDataListener(): void {
	if (saveDataListenerInstalled) return;
	const connection = getConnection();
	if (!connection?.addEventListener) {
		saveDataListenerInstalled = true;
		return;
	}
	connection.addEventListener('change', () => {
		const next = getConnection()?.saveData === true;
		if (next !== cachedSaveData) {
			cachedSaveData = next;
			saveDataListeners.forEach((listener) => listener(next));
		}
	});
	saveDataListenerInstalled = true;
}
function useSaveData(): boolean {
	const [saveData, setSaveData] = useState(() => getConnection()?.saveData === true);
	useEffect(() => {
		installSaveDataListener();
		const live = getConnection()?.saveData === true;
		if (live !== cachedSaveData) cachedSaveData = live;
		setSaveData(live);
		saveDataListeners.add(setSaveData);
		return () => {
			saveDataListeners.delete(setSaveData);
		};
	}, []);
	return saveData;
}
function useDocumentHidden(): boolean {
	const [hidden, setHidden] = useState<boolean>(typeof document === 'undefined' ? false : document.hidden);
	useEffect(() => {
		const handler = () => setHidden(document.hidden);
		document.addEventListener('visibilitychange', handler);
		return () => document.removeEventListener('visibilitychange', handler);
	}, []);
	return hidden;
}
function useWindowFocused(): boolean {
	const [focused, setFocused] = useState<boolean>(Window.isFocused() && Window.isVisible());
	useEffect(() => {
		const update = () => setFocused(Window.isFocused() && Window.isVisible());
		window.addEventListener('focus', update);
		window.addEventListener('blur', update);
		document.addEventListener('visibilitychange', update);
		update();
		return () => {
			window.removeEventListener('focus', update);
			window.removeEventListener('blur', update);
			document.removeEventListener('visibilitychange', update);
		};
	}, []);
	return focused;
}
type AllowanceMode = 'ALWAYS' | 'ON_INTERACTION' | 'NEVER';
function getKindAllowance(kind: ShouldAnimateKind): AllowanceMode {
	switch (kind) {
		case 'emoji':
			return UserSettings.getAnimateEmoji() ? 'ALWAYS' : 'ON_INTERACTION';
		case 'gif':
			return UserSettings.getGifAutoPlay() ? 'ALWAYS' : 'ON_INTERACTION';
		case 'sticker': {
			const value = UserSettings.getAnimateStickers();
			if (value === StickerAnimationOptions.ALWAYS_ANIMATE) return 'ALWAYS';
			if (value === StickerAnimationOptions.NEVER_ANIMATE) return 'NEVER';
			return 'ON_INTERACTION';
		}
		case 'avatar':
		case 'guild_icon':
		case 'banner':
		case 'custom_status_emoji':
			return 'ON_INTERACTION';
	}
}
export function useShouldAnimate({
	kind,
	isHovering = false,
	isFocused = false,
	entitlementOk,
}: UseShouldAnimateOptions): boolean {
	const saveData = useSaveData();
	const documentHidden = useDocumentHidden();
	const windowFocused = useWindowFocused();
	if (entitlementOk === false) return false;
	if (Accessibility.useReducedMotion) return false;
	if (documentHidden) return false;
	if (saveData) return false;
	if (!windowFocused && !Accessibility.stayInteractiveWhenUnfocused) return false;
	const allowance = getKindAllowance(kind);
	if (allowance === 'NEVER') return false;
	if (allowance === 'ALWAYS') return true;
	return isHovering || isFocused;
}
